import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';

/**
 * PasswordPrompt - A reusable password input modal that defaults to hidden text
 *
 * Usage:
 * <PasswordPrompt
 *   show={showModal}
 *   title="Enter Password"
 *   message="Please enter your password to continue"
 *   onSubmit={(password) => handlePassword(password)}
 *   onCancel={() => setShowModal(false)}
 *   confirmMode={false}  // Set to true for password creation with confirmation
 * />
 */
const PasswordPrompt = ({
  show,
  title = 'Enter Password',
  message = '',
  onSubmit,
  onCancel,
  confirmMode = false,  // If true, shows two fields for password creation
  submitLabel = 'Submit'
}) => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  // Focus on password input when modal opens
  useEffect(() => {
    if (show && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [show]);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (show) {
      setPassword('');
      setConfirmPassword('');
      setShowPassword(false);
      setShowConfirmPassword(false);
      setError('');
    }
  }, [show]);

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!password) {
      setError('Please enter a password');
      return;
    }

    if (confirmMode) {
      if (!confirmPassword) {
        setError('Please confirm your password');
        return;
      }
      if (password !== confirmPassword) {
        setError('Passwords do not match');
        return;
      }
    }

    onSubmit(password);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSubmit(e);
    }
  };

  return (
    <Modal show={show} onHide={onCancel} centered>
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {message && (
          <p style={{ marginBottom: '15px', color: '#666' }}>{message}</p>
        )}

        <Form onSubmit={handleSubmit}>
          {/* Password Field */}
          <Form.Group className="mb-3">
            <Form.Label>{confirmMode ? 'Password' : ''}</Form.Label>
            <div style={{ position: 'relative' }}>
              <Form.Control
                ref={inputRef}
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setError('');
                }}
                onKeyPress={handleKeyPress}
                placeholder="Enter password"
                style={{
                  paddingRight: '50px',
                  fontSize: '16px'  // Prevents iOS zoom
                }}
                autoComplete="off"
              />
              <Button
                variant="link"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  right: '5px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  padding: '0 10px',
                  color: '#666',
                  textDecoration: 'none',
                  fontSize: '14px'
                }}
                tabIndex={-1}
              >
                {showPassword ? 'Hide' : 'Show'}
              </Button>
            </div>
          </Form.Group>

          {/* Confirm Password Field (only in confirm mode) */}
          {confirmMode && (
            <Form.Group className="mb-3">
              <Form.Label>Confirm Password</Form.Label>
              <div style={{ position: 'relative' }}>
                <Form.Control
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    setError('');
                  }}
                  onKeyPress={handleKeyPress}
                  placeholder="Confirm password"
                  style={{
                    paddingRight: '50px',
                    fontSize: '16px'  // Prevents iOS zoom
                  }}
                  autoComplete="off"
                />
                <Button
                  variant="link"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  style={{
                    position: 'absolute',
                    right: '5px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    padding: '0 10px',
                    color: '#666',
                    textDecoration: 'none',
                    fontSize: '14px'
                  }}
                  tabIndex={-1}
                >
                  {showConfirmPassword ? 'Hide' : 'Show'}
                </Button>
              </div>
            </Form.Group>
          )}

          {/* Error Message */}
          {error && (
            <div style={{ color: '#dc3545', marginBottom: '15px', fontSize: '14px' }}>
              {error}
            </div>
          )}
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSubmit}>
          {submitLabel}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default PasswordPrompt;
